import React, { useEffect, useRef } from 'react';
import { WifiOff } from 'lucide-react';

/**
 * U-2 (G-OPS-2): reopening the app offline shows the app, not sign-in, for the web and mobile shells alike.
 *
 * The last signed-in user's profile and menu (the /api/platform/experience answer, per organisation and site) are
 * kept here, bound to the session by a fingerprint of its token. The token itself is never copied: it stays only where
 * sign-in already stores it. They are read only when the server cannot be reached (fetch rejects); a server that
 * answers and refuses the token still signs the user out, and sign-out forgets them. The server still checks every
 * request, so nothing here grants access; it only lets the shell open and the offline queues carry on.
 */
const KEY = 'fi_offline_session_v1';
const MENUS_KEPT = 5;
const SECRET_FIELD = /token|secret|password|otp|mfa|recovery|api_?key/i;

/** FNV-1a of the token plus its length: tells sessions apart without being the token or leading back to it. */
export function tokenFingerprint(token) {
  let hash = 0x811c9dc5;
  for (let i = 0; i < token.length; i += 1) {
    hash ^= token.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return `${hash.toString(16)}.${token.length}`;
}

function read() {
  try { return JSON.parse(localStorage.getItem(KEY)) || null; } catch { return null; }
}
function write(value) {
  try { localStorage.setItem(KEY, JSON.stringify(value)); } catch { /* full or blocked storage: an offline reopen falls back to sign-in */ }
}
function sessionFor(token) {
  const saved = token ? read() : null;
  return saved && saved.fp === tokenFingerprint(token) ? saved : null;
}
const withoutSecrets = (profile) => Object.fromEntries(Object.entries(profile || {}).filter(([key]) => !SECRET_FIELD.test(key)));

export function rememberUser(token, user) {
  if (!token || !user) return;
  const saved = sessionFor(token);
  write({ fp: tokenFingerprint(token), user: withoutSecrets(user), menus: saved?.menus || {}, saved_at: new Date().toISOString() });
}
export const rememberedUser = (token) => sessionFor(token)?.user || null;

/** The menu depends on the organisation, site and department chosen, as the experience request does. */
export const menuScope = () => ['current_organization_id', 'current_location_id', 'active_department']
  .map((key) => localStorage.getItem(key) || '').join('|');

export function rememberMenu(token, body, scope = menuScope()) {
  if (!token || !body) return;
  const saved = sessionFor(token);
  const menus = Object.entries({ ...(saved?.menus || {}), [scope]: undefined }).filter(([, v]) => v).slice(-(MENUS_KEPT - 1));
  write({ fp: tokenFingerprint(token), user: saved?.user || null, menus: Object.fromEntries([...menus, [scope, body]]),
    saved_at: saved?.saved_at || new Date().toISOString() });
}
export const rememberedMenu = (token, scope = menuScope()) => sessionFor(token)?.menus?.[scope] || null;

export function forgetOfflineSession() {
  try { localStorage.removeItem(KEY); } catch { /* ignore */ }
}

/** fetch rejects with a TypeError when the network is down or the server unreachable; an HTTP answer never does. */
export const isNetworkError = (error) => error?.name === 'TypeError';

/**
 * Who this token belongs to. `{ user }` when the server answers; `{ refused: true }` when it says no (the remembered
 * session is forgotten); `{ user, offline: true }` when it cannot be reached, `user` being the remembered one or null.
 */
export async function verifySession(url, token) {
  let response;
  try {
    response = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  } catch (error) {
    if (!isNetworkError(error)) throw error;
    return { user: rememberedUser(token), offline: true };
  }
  if (!response.ok) { forgetOfflineSession(); return { refused: true, status: response.status }; }
  const user = await response.json();
  rememberUser(token, user);
  return { user };
}

/** While a session was opened offline, check it again as soon as the device is back online. */
export function useReverifyWhenOnline(active, verify) {
  const latest = useRef(verify);
  latest.current = verify;
  useEffect(() => {
    if (!active) return undefined;
    const online = () => latest.current();
    window.addEventListener('online', online);
    return () => window.removeEventListener('online', online);
  }, [active]);
}

export function OfflineSessionBanner({ show }) {
  if (!show) return null;
  return (
    <div role="status" data-testid="offline-session-banner"
      className="flex items-center gap-2 px-4 py-2 text-sm bg-warn/10 text-accent-800 border-b border-surface-border">
      <WifiOff className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
      <span>Offline: showing your last session. Screens that work offline keep what you record and send it when you reconnect.</span>
    </div>
  );
}
